import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Coins, History, Pencil } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { RecordConflictAlert } from '@/components/feedback/RecordConflictAlert'
import { useToastStack } from '@/components/feedback/toast-stack'
import { EditableGrid } from '@/components/grid'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { useCashFirm, useInvalidateCash } from '@/features/cash/hooks'
import {
  bulkRow,
  gridRowsFromCaris,
  isRowDirty,
  planningGridColumns,
  planningGridSchema,
  PLANNING_GRID_FIELDS,
  type PlanningGridRow,
} from '@/features/cash/planning-grid'
import { AllocateSheet, HistoryPanel, ItemSheet } from '@/features/cash/PlanningSheets'
import { CashFirmSelect, CashStrip } from '@/features/cash/shared'
import { HrPageHeader as PageHeader } from '@/features/hr/HrPageHeader'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useQueryState } from '@/hooks/use-query-state'
import { formatDateOnly } from '@/lib/format'
import { recordChangedError, recordChangedMessage } from '@/lib/form/record-conflict'
import {
  countRowIssues,
  ROW_FIELD,
  rowErrorsProblem,
  rowIssuesFromProblem,
  rowIssuesFromSchema,
  type RowIssues,
} from '@/lib/form/row-errors'
import { useLeaveGuard } from '@/lib/form/use-leave-guard'
import { messages } from '@/lib/messages'
import { toProblem, type Problem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import { cashApi, type CashPlanItem, type CashPlanning } from '@/services/http/cash'

const t = messages.cash.planning

/** Görünüm süzgeçleri (kayıt kuralı değil): arama metni ve "yalnız açık bakiyeli" (`acik=0` = kapalı). */
const SEARCH_PARAM = 'ara'
const ONLY_OPEN_PARAM = 'acik'

function originalsOf(rows: readonly PlanningGridRow[]): Map<string, PlanningGridRow> {
  return new Map(rows.map((row) => [row.key, row]))
}

/**
 * Sunucudaki planı taslağa katar: kirli satırlar korunur, diğerleri güncel hâliyle değişir; artık
 * açık olmayan (DİA'da kapanan) kalemler düşer.
 */
function mergeRows(
  rows: readonly PlanningGridRow[],
  fresh: readonly PlanningGridRow[],
  originals: ReadonlyMap<string, PlanningGridRow>,
): PlanningGridRow[] {
  const current = new Map(rows.map((row) => [row.key, row]))
  return fresh.map((row) => {
    const old = current.get(row.key)
    return old && isRowDirty(old, originals.get(row.key)) ? old : row
  })
}

/** Satırın tamamına ait hata (`*`) kısımlar hücresinde gösterilir. */
function placeRowIssues(issues: RowIssues): RowIssues {
  return Object.fromEntries(
    Object.entries(issues).map(([key, fields]) => {
      const { [ROW_FIELD]: whole, ...rest } = fields
      return [key, whole && !rest.parts ? { ...rest, parts: whole } : rest]
    }),
  )
}

function PlanningGrid({
  firm,
  planning,
  canPlan,
  header,
}: {
  firm: string
  planning: CashPlanning
  canPlan: boolean
  /** Sayfa başlığı ve şerit; firma seçici kirli ızgarada önce onay sorar (`guard`). */
  header: (guard: (action: () => void) => void) => ReactNode
}) {
  const instruments = planning.instruments
  const [source, setSource] = useState(planning)
  const [originals, setOriginals] = useState(() => originalsOf(gridRowsFromCaris(planning.caris)))
  const [rows, setRows] = useState(() => gridRowsFromCaris(planning.caris))
  const [historyKey, setHistoryKey] = useState(0)
  // Süzgeçler adres çubuğundadır (geri/yenile korur); yazarken yalnız gecikmeli ve `replace` ile yazılır.
  const query = useQueryState()
  const [search, setSearch] = useState(() => query.get(SEARCH_PARAM) ?? '')
  const onlyOpen = query.get(ONLY_OPEN_PARAM) !== '0'
  const debouncedSearch = useDebouncedValue(search.trim(), 300)
  const pushedSearch = useRef(search.trim())
  useEffect(() => {
    if (debouncedSearch === pushedSearch.current) return
    pushedSearch.current = debouncedSearch
    query.set({ [SEARCH_PARAM]: debouncedSearch })
  }, [debouncedSearch, query])
  // Başarısız kayıt denemesinden sonra istemci doğrulaması canlı yenilenir.
  const [attempted, setAttempted] = useState(false)
  // Sunucu satır hataları, gönderilen satır nesnesiyle; satır değişince hatası düşer.
  const [serverIssues, setServerIssues] = useState<{
    issues: RowIssues
    sent: PlanningGridRow[]
  }>({ issues: {}, sent: [] })
  const [conflict, setConflict] = useState<Problem | null>(null)
  const [editing, setEditing] = useState<CashPlanItem | null>(null)
  const [allocating, setAllocating] = useState<string | null>(null)
  const [historyItem, setHistoryItem] = useState<CashPlanItem | null>(null)
  const { toast } = useToastStack()
  const invalidate = useInvalidateCash()
  const queryClient = useQueryClient()

  // Arka plan tazelemesi (kayıt, ayrıntı editörü, dağıtım, başka sekme) taslağa katılır.
  if (planning !== source) {
    const fresh = gridRowsFromCaris(planning.caris)
    setSource(planning)
    setOriginals(originalsOf(fresh))
    setRows(mergeRows(rows, fresh, originals))
  }

  const openCaris = useMemo(
    () => new Set(planning.caris.filter((c) => c.open_total > 0.005).map((c) => c.code)),
    [planning],
  )
  const visible = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr')
    return rows.filter(
      (row) =>
        (!onlyOpen || openCaris.has(row.cariCode)) &&
        (!q ||
          row.cariCode.toLocaleLowerCase('tr').includes(q) ||
          row.cariName.toLocaleLowerCase('tr').includes(q) ||
          (row.item.doc_no ?? '').toLocaleLowerCase('tr').includes(q)),
    )
  }, [rows, search, onlyOpen, openCaris])

  const dirtyRows = useMemo(
    () => rows.filter((row) => isRowDirty(row, originals.get(row.key))),
    [rows, originals],
  )
  const dirty = dirtyRows.length > 0
  const columns = useMemo(() => planningGridColumns(instruments), [instruments])
  const schema = useMemo(() => planningGridSchema(instruments), [instruments])

  const validate = useCallback(
    (list: PlanningGridRow[]) =>
      rowIssuesFromSchema(list, schema, (row) => row.key, PLANNING_GRID_FIELDS),
    [schema],
  )
  const issues = useMemo(() => {
    const live = new Set(rows)
    const server = Object.fromEntries(
      Object.entries(serverIssues.issues).filter(([key]) =>
        serverIssues.sent.some((row) => row.key === key && live.has(row)),
      ),
    )
    const client = attempted ? validate(dirtyRows) : {}
    return placeRowIssues({ ...server, ...client })
  }, [rows, dirtyRows, attempted, serverIssues, validate])

  // Süzgeçli görünümün değişikliği tam listeye anahtarla katılır.
  const onRowsChange = (next: PlanningGridRow[]) => {
    const changed = new Map(next.map((row) => [row.key, row]))
    setRows((old) => old.map((row) => changed.get(row.key) ?? row))
  }

  const reset = () => {
    setAttempted(false)
    setServerIssues({ issues: {}, sent: [] })
    setConflict(null)
    setHistoryKey((k) => k + 1)
  }
  const discard = () => {
    setRows(gridRowsFromCaris(planning.caris))
    reset()
  }
  const guard = useLeaveGuard(dirty, discard)

  const save = useMutation({
    mutationFn: (sent: PlanningGridRow[]) =>
      cashApi.bulkPlans(
        firm,
        sent.map((row) => bulkRow(row, instruments)),
      ),
    onSuccess: (saved) => {
      // Kaydedilen satırlar yeni sürümle temiz sayılır; tazeleme gelince sunucu hâliyle değişir.
      const versions = new Map(saved.map((s) => [s.key, s.version]))
      const next = rows.map((row) => {
        const version = versions.get(row.key)
        return version === undefined ? row : { ...row, version }
      })
      setRows(next)
      setOriginals((old) => {
        const copy = new Map(old)
        for (const row of next) if (versions.has(row.key)) copy.set(row.key, row)
        return copy
      })
      reset()
      toast({ type: 'success', title: t.grid.saved(saved.length) })
      void invalidate()
    },
    onError: (error, sent) => {
      const rowProblem = rowErrorsProblem(error)
      if (rowProblem) {
        const found = rowIssuesFromProblem(
          rowProblem,
          sent.map((row) => row.key),
          PLANNING_GRID_FIELDS,
        )
        setServerIssues({ issues: found, sent })
        toast({ type: 'error', title: t.grid.invalid(countRowIssues(found)) })
        return
      }
      const stale = recordChangedError(error)
      if (stale) {
        setConflict(stale)
        const row = stale.row === undefined ? undefined : sent[stale.row]
        if (row) {
          setServerIssues({
            issues: { [row.key]: { [ROW_FIELD]: recordChangedMessage(stale) } },
            sent,
          })
        }
        return
      }
      toast({ type: 'error', title: t.failed, description: toProblem(error).detail ?? undefined })
    },
  })

  const onSave = () => {
    const sent = dirtyRows
    if (sent.length === 0) return
    setAttempted(true)
    const found = validate(sent)
    if (countRowIssues(found) > 0) {
      toast({ type: 'error', title: t.grid.invalid(countRowIssues(found)) })
      return
    }
    save.mutate(sent)
  }

  const reload = useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        queryKey: queryKeys.cash.planning(firm),
        queryFn: () => cashApi.planning(firm),
        staleTime: 0,
      }),
    onSuccess: (data) => {
      // Bayat satırlar güncel hâline döner; diğer düzenlemeler korunur.
      const fresh = gridRowsFromCaris(data.caris)
      const current = new Map(rows.map((row) => [row.key, row]))
      setOriginals(originalsOf(fresh))
      setRows(
        fresh.map((row) => {
          const old = current.get(row.key)
          return old && old.version === row.version ? old : row
        }),
      )
      setSource(data)
      setConflict(null)
      setServerIssues({ issues: {}, sent: [] })
    },
  })

  const cari = allocating ? (planning.caris.find((c) => c.code === allocating) ?? null) : null

  return (
    <>
      {header(guard.confirm)}
      {guard.dialog}
      <CashStrip points={planning.strip} />
      <div className="flex flex-wrap items-center gap-3">
        <Input
          className="w-72"
          placeholder={t.search}
          aria-label={t.search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={onlyOpen}
            onCheckedChange={(v) => query.set({ [ONLY_OPEN_PARAM]: v ? undefined : '0' })}
          />
          {t.onlyOpen}
        </label>
        <span className="text-text-secondary text-xs">
          {messages.cash.weekly.start(formatDateOnly(planning.start))}
        </span>
      </div>
      {conflict && (
        <RecordConflictAlert
          label={recordChangedMessage(conflict)}
          onReload={() => reload.mutate()}
          pending={reload.isPending}
          failed={reload.isError}
        />
      )}
      {canPlan && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-text-secondary mr-auto text-xs">{t.grid.hint}</p>
          <Button variant="ghost" disabled={!dirty} onClick={() => guard.confirm(discard)}>
            {t.grid.discard}
          </Button>
          <Button disabled={!dirty} loading={save.isPending} onClick={onSave}>
            {t.grid.save(dirtyRows.length)}
          </Button>
        </div>
      )}
      {visible.length === 0 ? (
        <EmptyState title={t.noCaris} description={t.description} />
      ) : (
        <EditableGrid
          ariaLabel={t.grid.label}
          rows={visible}
          columns={columns}
          rowKey={(row) => row.key}
          onRowsChange={onRowsChange}
          issues={issues}
          historyKey={historyKey}
          readOnly={!canPlan}
          className="max-h-[70vh]"
          rowState={(row) => (isRowDirty(row, originals.get(row.key)) ? 'dirty' : undefined)}
          rowActions={(row) => {
            const doc = row.item.doc_no ?? row.key
            return (
              <>
                {canPlan && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`${t.edit} ${doc}`}
                    disabled={isRowDirty(row, originals.get(row.key))}
                    onClick={() => setEditing(row.item)}
                  >
                    <Pencil />
                  </Button>
                )}
                {canPlan && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`${t.grid.allocate} ${row.cariName}`}
                    onClick={() => setAllocating(row.cariCode)}
                  >
                    <Coins />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`${t.history} ${doc}`}
                  onClick={() => setHistoryItem(row.item)}
                >
                  <History />
                </Button>
              </>
            )
          }}
        />
      )}
      {/* Her açılışta editör seçilen kalemden yeniden kurulur. */}
      <ItemSheet
        key={editing?.key ?? 'kapali'}
        firm={firm}
        item={editing}
        instruments={instruments}
        onClose={() => setEditing(null)}
      />
      <AllocateSheet
        firm={firm}
        cari={cari}
        instruments={instruments}
        onClose={() => setAllocating(null)}
      />
      <HistoryPanel firm={firm} item={historyItem} onClose={() => setHistoryItem(null)} />
    </>
  )
}

/** /nakit/odeme-planlama — cari kalemlerin ödeme planı (docs/ODEME-PLANLAMA-IZGARA-PLAN.md Faz 2). */
export function PlanningPage() {
  const { firms, firm, setFirm, can } = useCashFirm()
  const canPlan = can('cash.plan')
  const planning = useQuery({
    queryKey: queryKeys.cash.planning(firm ?? ''),
    queryFn: () => cashApi.planning(firm!),
    enabled: !!firm,
  })

  const error = firms.error ?? planning.error
  const pageHeader = (guard: (action: () => void) => void) => (
    <PageHeader
      title={t.title}
      description={t.description}
      actions={
        <CashFirmSelect
          firms={firms.data ?? []}
          firm={firm}
          onChange={(next) => guard(() => setFirm(next))}
        />
      }
    />
  )

  if (!planning.data || !firm || error) {
    return (
      <div className="flex flex-col gap-4">
        {pageHeader((action) => action())}
        {error ? (
          <ErrorState
            error={error}
            onRetry={() => void (firms.isError ? firms.refetch() : planning.refetch())}
          />
        ) : (
          <Loading rows={8} />
        )}
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-4">
      <PlanningGrid
        key={firm}
        firm={firm}
        planning={planning.data}
        canPlan={canPlan}
        header={pageHeader}
      />
    </div>
  )
}
